import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { DEFAULT_CURRENCY } from '@constants/currency';

import { getCustomerStructureTree } from '@helpers/getCustomerStructureTree';

import type { ICurrency } from '@model/currency';
import type { ILocation, ILocationStructureNode } from '@model/structureData';

import { apiClient } from '@services/apiClient';

import { selectAppPersistRefreshInterval } from '@store/useAppStorePersist';

interface IResponse {
  data: ILocation[];
  locations: Record<string, string>;
}

interface IResult {
  allowedIds: Record<string, string>;
  customerLevelId: number | null;
  facilities: ILocation[];
  locations: ILocationStructureNode[];
  locationsIds: Array<number>;
  facilitiesIds: Array<number>;
  customerCurrency: ICurrency;
}

export const assetTreeKey = () => ['assetTree'];
export const assetTreeFn = () => apiClient.get(`${APP_API_URL_V2}fsd/asset-tree`);

const assetTreeSelect = (data: IResponse): IResult => {
  if (!data?.data?.length) {
    return {
      allowedIds: {},
      customerLevelId: null,
      facilities: [],
      locations: [],
      locationsIds: [],
      facilitiesIds: [],
      customerCurrency: DEFAULT_CURRENCY,
    };
  }

  const location = data.data[0];

  const { facilities, facilitiesIds, locations, locationsIds } = getCustomerStructureTree(
    location.children,
    data.locations,
  );

  return {
    allowedIds: data?.locations,
    customerLevelId: location.customerId,
    facilities,
    facilitiesIds,
    locations,
    locationsIds,
    customerCurrency: location.customer.currency || DEFAULT_CURRENCY,
  };
};

export const useAssetTreeQuery = () => {
  const refreshInterval = selectAppPersistRefreshInterval();

  return useQuery({
    queryKey: assetTreeKey(),
    queryFn: assetTreeFn,
    select: ({ data }: AxiosResponse<IResponse>) => assetTreeSelect(data),
    refetchInterval: refreshInterval,
  });
};
